import { defineStore } from 'pinia'
import { CustomerTypeEnum } from '../Enums';

export const useWizardStore = defineStore('WizardStore', {

    //State
    state: () => {
        return {
            inited: false,
            errorMessages: [],
            customerType: null,
            fuseType: null,
            yearlyConsumption: null,
            accomodationType: '',            
            monthlyConsumption: null,
            monthlyPowerPeak: null,      
            currentStep: 1,
            completedSteps: 0,
            numberOfSteps: 4,
            showResults: false,
            includeVat: false,
            calculatedPrice: {
                monthly: null,
                total: null
            },
            appSettings: {},                               
        }
    },

    //Actions
    actions: {
        initApp(settings) {
            if(settings){
                this.appSettings = settings;

                //Validate settings
                let validSettings = true;
                var requiredData = ["CustomerTypeList", "FuseList", "CustomerTypeInfoList"];
                requiredData.forEach(data => {
                    if(!this.appSettings[data] || this.appSettings[data].length === 0) {
                        this.errorMessages.push(data + " saknas eller innehåller inga värden.");
                        validSettings = false;
                    }                
                });
                if(validSettings) {
                    this.resetMonthlyConsumption();
                    this.resetMonthlyPowerPeak();
                    this.updateMissingTextsWithDefaultValues();
                    this.inited = true;
                }
            } else {
                this.errorMessages.push("Det saknas inställningar för att kunna initiera appen.");
            }
        },
        updateMissingTextsWithDefaultValues() {
            this.appSettings.ElNetworkModuleStep1.Title ??= 'Välj vilken typ av kund du är';
            this.appSettings.ElNetworkModuleStep2.Title ??= 'Välj din storlek på säkring';
            this.appSettings.ElNetworkModuleStep3.Title ??= 'Fyll i din årsförbrukning';
            this.appSettings.ElNetworkModuleStep4.Title ??= 'Vad är dina högsta effekttoppar?';
            this.appSettings.Result.Title ??= 'Uträkning';
            this.appSettings.NextStepButtonText ??= 'Gå vidare';
            this.appSettings.UpdateButtonText ??= 'Uppdatera';
            this.appSettings.CalculateButtonText ??= 'Se uträkning';
            this.appSettings.FixedChargeText ??= 'Fast avgift';
            this.appSettings.TransferCostText ??= 'Överföringsavgift';
            this.appSettings.EnergyTaxCostText ??= 'Energiskatt';
            this.appSettings.EffectCostText ??= 'Effektavgift';                       
        },
        gotoWizardStep(step) {
            this.currentStep = step;
        },
        gotoNextWizardStep() {
            //Go from step 1?            
            if(this.currentStep === 1) {

                this.includeVat = this.customerType !== CustomerTypeEnum.COMPANY;
                
                //Apartment, skip step 2
                if(this.customerType === CustomerTypeEnum.APPARTMENT) {
                    this.completedSteps = 2;
                    this.currentStep = 3;
                    
                    //Select fuse automatically
                    if(this.fuseOptionsForSelectedCustomerType.length === 0) {
                        this.errorMessages.push("Det finns inga säkringar för vald kundtyp");
                    }
                    this.fuseType = this.fuseOptionsForSelectedCustomerType[0].Id;
                    return;
                } else {
                    //Automatically select first fuse
                    if(this.fuseOptionsForSelectedCustomerType.length > 0) {
                        this.fuseType = this.fuseOptionsForSelectedCustomerType[0].Id;
                        if(this.customerType === CustomerTypeEnum.COMPANY) {
                            this.updateYearlyConsumptionBasedOnSelectedFuseType();
                            this.updateMonthlyConsumptionBasedOnYearlyConsumption();
                            this.updateMonthlyPowerPeakBasedOnYearlyConsumption();                            
                        }
                    }
                }
            }

            this.currentStep++
            this.completedSteps = Math.max(this.currentStep - 1, this.completedSteps);
        },
        calculateAndShowResult() {
            this.calculateNewPrice();
            this.currentStep++
            this.showResults = true;
        },
        setCompletedSteps(newValue) {
            this.completedSteps = newValue;
        },
        resetMonthlyConsumption() {
            //Create object with 12 months and set all values to 0
            this.monthlyConsumption = Object.fromEntries(Array.from({ length: 12 }, (_, i) => [i + 1, 0]));
        },
        resetMonthlyPowerPeak() {
            //Create object with 12 months and set all values to 0
            this.monthlyPowerPeak = Object.fromEntries(Array.from({ length: 12 }, (_, i) => [i + 1, 0]));           
        },
        resetFormDataThatDependsOnCustomerType() {
            this.fuseType = null;
            this.resetFormDataThatDependsOnFuseType();
            this.completedSteps = 0;
            this.showResults = false;
        },
        resetFormDataThatDependsOnFuseType() {
            this.yearlyConsumption = null;
            this.accomodationType = '';               
            this.resetMonthlyPowerPeak();
            this.resetMonthlyConsumption();
            this.errorMessages = [];
            this.completedSteps = 1;
        },        
        setYearlyConsumption(newValue) {
            this.yearlyConsumption = newValue;
        },
        setYearlyConsumptionAsSumOfMonthlyConsumtions() {
            let sum = 0;
            for (let monthNumber = 1; monthNumber <= 12; monthNumber++) {
                sum += Number.parseFloat(this.monthlyConsumption[monthNumber]);      
            }
            this.yearlyConsumption = Math.round(sum);
        },
        updateMonthlyConsumptionBasedOnYearlyConsumption() {
            try {
                let monthlyEnergyChargeData = this.monthlyEnergyChargeDataForSelectedFuse;
                let monthlyConsumption = {};
                Object.keys(this.monthlyConsumption).forEach(key => {
                    let monthData = monthlyEnergyChargeData.FixedCharge.find(x => x.Month === parseInt(key));
                    monthlyConsumption[key] = this.yearlyConsumption * monthData.Value;
                });            
                this.monthlyConsumption = monthlyConsumption;
            } catch(e){
                console.error("Failed to update monthly consumption based on accomodation type", e);
            }            
        },
        updateMonthlyPowerPeakBasedOnYearlyConsumption() {  
            try { 
                let monthlyEffectChargeData = this.monthlyEffectChargeDataForSelectedFuse;
                let monthlyPowerPeak = {};
                Object.keys(this.monthlyPowerPeak).forEach(key => {
                    let monthData = monthlyEffectChargeData.FixedCharge.find(x => x.Month === parseInt(key));
                    const monthConsumption = this.monthlyConsumption[key];
                    monthlyPowerPeak[key] = monthConsumption * monthData.Value;
                });            
                this.monthlyPowerPeak = monthlyPowerPeak;
            } catch(e){
                console.error("Failed to update monthly power peak based on accomodation type", e);
            }
        },
        updateYearlyConsumptionBasedOnSelectedFuseType() {        
            try {
                let accomodationType = this.appSettings.CustomerTypeInfoList.find(x => x.FuseId === this.fuseType);
                this.yearlyConsumption = Number.parseInt(parseFloat(accomodationType.YearsConsumption).toFixed(0));
            } catch(e){
                console.error("Failed to update yearly consumption based on selected fuse", e);
            }
        },
        updateYearlyConsumptionBasedOnSelectedAccomodationType() {            
            try {
                let accomodationType = this.appSettings.CustomerTypeInfoList.find(x => x.Id === this.accomodationType);
                this.yearlyConsumption = Number.parseInt(parseFloat(accomodationType.YearsConsumption).toFixed(0));       
            } catch(e){
                console.error("Failed to update yearly consumption based on selected accomodationType", e);
            }
        },  
        calculateNewPrice() {            
            //Get selected fuse
            let fuse = this.appSettings.FuseList.find(x => x.Id === this.fuseType);
                        
            let newMonthlyPrice = {};
            let newTotalPrice = {
                FixedCharge: 0,
                TransferCost: 0,
                EnergyTaxCost: 0,
                EffectCost: 0,             
                TotalCost: 0,             
            };
            try {                
                for (let monthNumber = 1; monthNumber <= 12; monthNumber++) {
                    let monthlyConsumption = this.monthlyConsumption[monthNumber];
                    let monthlyPowerPeak = this.monthlyPowerPeak[monthNumber];    

                    let monthlyPrice = {
                        FixedCharge: fuse.FixedCharge.FixedCharge.find(x => x.Month === monthNumber).Value,
                        TransferCost: fuse.TransferCost.FixedCharge.find(x => x.Month === monthNumber).Value * monthlyConsumption,
                        EnergyTaxCost: fuse.EnergyTaxCost.FixedCharge.find(x => x.Month === monthNumber).Value * monthlyConsumption,
                        EffectCost: fuse.EffectCost.FixedCharge.find(x => x.Month === monthNumber).Value * monthlyPowerPeak,
                    };

                    //Include vat?
                    if(this.includeVat){
                        const VAT_MULTIPLIER = 1.25;
                        monthlyPrice.FixedCharge = monthlyPrice.FixedCharge * VAT_MULTIPLIER;
                        monthlyPrice.TransferCost = monthlyPrice.TransferCost * VAT_MULTIPLIER;
                        monthlyPrice.EnergyTaxCost = monthlyPrice.EnergyTaxCost * VAT_MULTIPLIER;
                        monthlyPrice.EffectCost = monthlyPrice.EffectCost * VAT_MULTIPLIER;
                    }

                    newMonthlyPrice[monthNumber] = monthlyPrice;
                    //Update total price
                    newTotalPrice.FixedCharge += monthlyPrice.FixedCharge;
                    newTotalPrice.TransferCost += monthlyPrice.TransferCost;
                    newTotalPrice.EnergyTaxCost += monthlyPrice.EnergyTaxCost;
                    newTotalPrice.EffectCost += monthlyPrice.EffectCost;
                };
                newTotalPrice.TotalCost = newTotalPrice.FixedCharge + newTotalPrice.TransferCost + newTotalPrice.EnergyTaxCost + newTotalPrice.EffectCost;
                this.calculatedPrice.monthly = newMonthlyPrice;
                this.calculatedPrice.total = newTotalPrice;
            } catch(e) {
                console.error("Failed to calculate new price", e);
                this.calculatedPrice.monthly = null;
                this.calculatedPrice.total = null;                
            }

        }
    },

    //Getters   
    getters: {
        showStep2() {
            return this.completedSteps >= 1 && (this.customerType === CustomerTypeEnum.HOUSE || this.customerType === CustomerTypeEnum.COMPANY)
        },
        showStep3() {
            return this.completedSteps >= 2;
        },
        showStep4() {
            return this.completedSteps >= 3 && (this.customerType === CustomerTypeEnum.HOUSE || this.customerType === CustomerTypeEnum.COMPANY) && this.fuseType !== null;
        },     
        showCalculationButton() {
            if(this.customerType === CustomerTypeEnum.APPARTMENT) {
                return this.currentStep == 2;
            }

            return this.currentStep == this.numberOfSteps;
        },
        showEffectCostInResult() {
            return this.customerType !== CustomerTypeEnum.APPARTMENT;
        },
        selectedCustomerTypeName() {
            let customerType = this.appSettings.CustomerTypeList.find(x => x.Id === this.customerType);
            return customerType ? customerType.Name : this.customerType;
        },
        selectedFuseName() {
            let fuse = this.appSettings.FuseList.find(x => x.Id === this.fuseType);
            return fuse ? fuse.Name : null;
        },
        fuseOptionsForSelectedCustomerType() {
            //Filter CustomerTypeInfoList by CustomerTypeIdList and return thos containing the selected customerType
            let filteredCustomerTypeInfoList = this.appSettings.CustomerTypeInfoList.filter(x => x.CustomerTypeIdList.includes(this.customerType));

            //Map the filteredCustomerTypeInfoList to FuseList
            let fuseList = this.appSettings.FuseList.filter(x => filteredCustomerTypeInfoList.map(y => y.FuseId).includes(x.Id));
            return fuseList;
        },
        accomodationTypesForSelectedCustomerTypeAndFuse() {
            //Filter CustomerTypeInfoList by CustomerTypeIdList and return thos containing the selected customerType
            let filteredCustomerTypeInfoList = this.appSettings.CustomerTypeInfoList.filter(x => x.CustomerTypeIdList.includes(this.customerType));            

            //Filter CustomerTypeInfoList by FuseId and return thos containing the selected fuseType
            filteredCustomerTypeInfoList = filteredCustomerTypeInfoList.filter(x => x.FuseId === this.fuseType);

            return filteredCustomerTypeInfoList;
        },
        yearlyConsumptionForSelectedAccomodationType() {
            if(this.accomodationType === null && this.accomodationType !== '') {
                return null;
            }
            let accomodationType = this.appSettings.CustomerTypeInfoList.find(x => x.Id === this.accomodationType);
            return accomodationType ? accomodationType.YearsConsumption : null;
        },
        monthlyEnergyChargeDataForSelectedFuse() {
            if(this.fuseType === null) {
                return null;
            }
            //Get selected fuse
            let fuse = this.appSettings.FuseList.find(x => x.Id === this.fuseType);
            let energyCharge = fuse.EnergyCharge;
            //Clone energyCharge and update with kwh/month
            let energyChargeData = JSON.parse(JSON.stringify(energyCharge)); 
           // let yearlyConsumption = this.yearlyConsumptionForSelectedAccomodationType;          
            energyChargeData.FixedCharge.forEach(monthData => {
                energyChargeData.FixedAbsoluteCharge = {'Month': monthData.Month, 'Value': monthData.Value * this.yearlyConsumption};
            });    
            return energyChargeData;
        },
        monthlyEffectChargeDataForSelectedFuse() {
            if(this.fuseType === null) {
                return null;
            }
            //Get selected fuse
            let fuse = this.appSettings.FuseList.find(x => x.Id === this.fuseType);
            let effectCharge = fuse.EffectCharge;
            //Clone effectCharge and update with kwh/month
            let effectChargeData = JSON.parse(JSON.stringify(effectCharge));           
            //let yearlyConsumption = this.yearlyConsumptionForSelectedAccomodationType;
            effectChargeData.FixedCharge.forEach(monthData => {
                effectChargeData.FixedAbsoluteCharge = {'Month': monthData.Month, 'Value': monthData.Value * this.yearlyConsumption};
            });    
            return effectChargeData;
        },
    }
});
